import { round } from "@ugg/shared/utils/math";
import { RoleC, RoleN, RoleS } from "@ugg/shared/utils/role-helpers";
import { RegionC, RegionN } from "@ugg/shared/utils/region-helpers";
import { RankC, RankN } from "@ugg/shared/utils/rank-helpers";
import { getKey, getData } from "./common";

import { Duos, DuoChampionsData, DuoChampion } from "@ugg/shared/interfaces/champions/duos.interface";

export interface DuosInfo {
  duos: {
    role: DuoChampion[10];
    champion_id: DuoChampion[0];
    champion_link: {
      champion_id: DuoChampion[0];
      role: DuoChampion[10];
    };
    wins: DuoChampion[1];
    matches: DuoChampion[2];
    win_rate: number;
    pick_rate: number;
    tier: {
      pick_rate: number;
      win_rate: number;
    };
    xp_adv_15: DuoChampion[3];
    gold_adv_15: DuoChampion[4];
    cs_adv_15: DuoChampion[5];
    kill_adv_15: DuoChampion[6];
    carry_percentage_15: DuoChampion[7];
    duo_carry_percentage_15: DuoChampion[8];
    team_gold_difference_15: DuoChampion[9];
  }[];
  last_updated: DuoChampionsData[1];
}

export function cleanDuosData(data: Duos) {
  const cleanData: Record<string, DuosInfo> = {};
  const regionKeys: RegionN[] = Object.keys(data || {}).map(Number);
  for (const regionId of regionKeys) {
    const regionData = data[regionId];
    const rankKeys: RankN[] = Object.keys(regionData || {}).map(Number);

    for (const rankId of rankKeys) {
      const rankData = regionData[rankId];
      const roleKeys: RoleN[] = Object.keys(rankData || {}).map(Number);

      for (const roleId of roleKeys) {
        const roleData = rankData[roleId];
        const data = roleData[0];
        const last_updated = roleData[1];

        let totalMatches = 0;

        const duos: DuosInfo["duos"] = data.map((duoChampion) => {
          const matches = duoChampion[2];
          totalMatches += matches;
          return {
            role: duoChampion[10],
            champion_id: duoChampion[0],
            champion_link: {
              champion_id: duoChampion[0],
              role: duoChampion[10],
            },
            pick_rate: 0,
            tier: {
              pick_rate: 0,
              win_rate: 0,
            },
            wins: duoChampion[1],
            matches: duoChampion[2],
            win_rate: round((duoChampion[1] / duoChampion[2]) * 100, 2),
            xp_adv_15: round(duoChampion[3] / matches, 0),
            gold_adv_15: round(duoChampion[4] / matches, 0),
            cs_adv_15: round(duoChampion[5] / matches, 1),
            kill_adv_15: round(duoChampion[6] / matches, 2),
            carry_percentage_15: round((duoChampion[7] / matches) * 10, 0),
            duo_carry_percentage_15: round((duoChampion[8] / matches) * 10, 0),
            team_gold_difference_15: round(duoChampion[9] / matches),
          };
        });

        duos.forEach((duo) => {
          const pick_rate = round((duo.matches / totalMatches) * 100, 2);
          duo["pick_rate"] = pick_rate;
          duo["tier"] = {
            pick_rate,
            win_rate: duo.win_rate,
          };
        });

        duos.sort((a, b) => {
          if (a.pick_rate >= 0.5 && b.pick_rate >= 0.5) {
            if (a.win_rate > b.win_rate) return 1;
            else if (a.win_rate < b.win_rate) return -1;
            return 0;
          } else if (a.pick_rate >= 0.5 && b.pick_rate < 0.5) {
            return 1;
          } else if (a.pick_rate < 0.5 && b.pick_rate >= 0.5) {
            return -1;
          } else if (a.pick_rate < 0.5 && b.pick_rate < 0.5) {
            if (a.win_rate > b.win_rate) return 1;
            else if (a.win_rate < b.win_rate) return -1;
            return 0;
          }
          return 0;
        });

        const championDuos = {
          duos,
          last_updated,
        };

        const key = getKey(RegionC.convertToString(regionId), RankC.convertToString(rankId), RoleC.convertToString(roleId));

        cleanData[key] = championDuos;
      }
    }
  }

  return cleanData;
}

export function getDuos(
  data: ReturnType<typeof cleanDuosData>,
  params: {
    queueType: string;
    region: string;
    rank?: string;
    role?: string;
  },
) {
  return getData(data, params);
}
